/**
 * GraphQL Mutations and Queries
 * Centralized location for all GraphQL operations used in Build My Kubota
 */

// ============================================================================
// AUTHENTICATION MUTATIONS
// ============================================================================

export const GQ_LOGIN = `
  mutation Login($username: String!, $password: String!) {
    login(input: { username: $username, password: $password }) {
      token {
        accessToken
        refreshToken
        tokenType
        expiresIn
        refreshExpiresIn
        user {
          id
          username
          email
          firstName
          lastName
          displayName
          phoneNumber
          roles
          registeredDate
        }
      }
    }
  }
`;

export const GQ_LOGIN_WITH_GOOGLE = `
  mutation LoginWithGoogle($input: LoginWithGoogleInput!) {
    loginWithGoogle(input: $input) {
      token {
        accessToken
        refreshToken
        tokenType
        expiresIn
        refreshExpiresIn
        user {
          id
          email
          firstName
          lastName
          username
          roles
        }
      }
    }
  }
`;

export const GQ_LOGOUT = `
  mutation Logout {
    logout {
      success
      message
    }
  }
`;

export const GQ_REFRESH_TOKEN = `
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(input: { refreshToken: $refreshToken }) {
      token {
        accessToken
        refreshToken
        tokenType
        expiresIn
        refreshExpiresIn
        user {
          id
          username
          email
          firstName
          lastName
          displayName
          phoneNumber
          roles
          registeredDate
        }
      }
    }
  }
`;

// ============================================================================
// USER REGISTRATION MUTATIONS
// ============================================================================

export const GQ_SEND_VERIFICATION_CODE = `
  mutation SendVerificationCode($email: String!) {
    sendVerificationCode(input: { email: $email }) {
      success
      message
      expiresIn
    }
  }
`;

export const GQ_VERIFY_CODE = `
  mutation VerifyCode($email: String!, $code: String!) {
    verifyCode(input: { email: $email, code: $code }) {
      success
      message
      email
    }
  }
`;

export const GQ_COMPLETE_REGISTRATION = `
  mutation CompleteRegistration(
    $email: String!
    $password: String!
    $firstName: String!
    $lastName: String!
    $phoneNumber: String!
  ) {
    completeRegistration(input: {
      email: $email
      password: $password
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
    }) {
      success
      message
      token {
        user {
          email
          firstName
          lastName
          phoneNumber
        }
        accessToken
        refreshToken
        expiresIn
        refreshExpiresIn
        tokenType
      }
    }
  }
`;

// ============================================================================
// PASSWORD RESET MUTATIONS
// ============================================================================

export const GQ_SEND_RESET_CODE = `
  mutation SendResetCode($email: String!) {
    sendResetCode(input: { email: $email }) {
      success
      message
      expiresIn
    }
  }
`;

export const GQ_VERIFY_RESET_CODE = `
  mutation VerifyResetCode($email: String!, $code: String!) {
    verifyResetCode(input: { email: $email, code: $code }) {
      success
      message
    }
  }
`;

export const GQ_COMPLETE_PASSWORD_RESET = `
  mutation CompletePasswordReset($email: String!, $newPassword: String!) {
    completePasswordReset(input: {
      email: $email
      newPassword: $newPassword
    }) {
      success
      message
    }
  }
`;

export const GQ_CHANGE_PASSWORD = `
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(input: {
      currentPassword: $currentPassword
      newPassword: $newPassword
    }) {
      success
      message
    }
  }
`;

// ============================================================================
// USER PROFILE MUTATIONS
// ============================================================================

export const GQ_UPDATE_USER_PROFILE = `
  mutation UpdateUserProfile($firstName: String, $lastName: String, $phoneNumber: String) {
    updateUserProfile(input: { 
      firstName: $firstName
      lastName: $lastName
      phoneNumber: $phoneNumber
    }) {
      success
      message
    }
  }
`;

// ============================================================================
// USER QUERIES
// ============================================================================

export const GQ_ME_QUERY = `
  query Me {
    me {
      id
      username
      email
      firstName
      lastName
      displayName
      phoneNumber
      roles
      registeredDate
    }
  }
`;

// ============================================================================
// BUILD MANAGEMENT MUTATIONS
// ============================================================================

export const GQ_SAVE_BUILD = `
  mutation SaveBuild(
    $productURL: String!
    $savedURL: String!
    $configuredItems: String!
    $buildName: String!
    $uc: String!
    $image: String!
    $un: String
  ) {
    saveBuild(input: {
      productURL: $productURL
      savedURL: $savedURL
      configuredItems: $configuredItems
      buildName: $buildName
      uc: $uc
      image: $image
      un: $un
    }) {
      build {
        id
        userId
        un
        uc
        buildName
        productURL
        savedURL
        image
        configuredItems
      }
    }
  }
`;

// ============================================================================
// BUILD QUERIES
// ============================================================================

export const GQ_MY_BUILDS = `
  query MyBuilds {
    myBuilds {
      id
      userId
      un
      uc
      buildName
      productURL
      savedURL
      image
      configuredItems
    }
  }
`;

export const GQ_GET_BUILD = `
  query GetBuild($id: String!) {
    build(id: $id) {
      id
      userId
      un
      uc
      buildName
      productURL
      savedURL
      image
      configuredItems
    }
  }
`;
